/**
 * Colour swatches from what an admin actually types: a name ("Navy", "Heather
 * Grey", "light blue") or an HTML code ("#1f2a44", "1f2a44", "#abc").
 *
 * Names resolve through a short list of apparel colours first (these read
 * better on clothing than the CSS keywords — CSS "navy" is a bright #000080),
 * then any CSS colour name the browser knows. Everything is stored as #rrggbb.
 */

// WWG brand colours (Dylan, BugHerd #90). Listed first; these are the ones
// used most often. Their names win over the general list below.
export const BRAND_COLOURS = {
  "Light Blue": "#91b6bb",
  Blue: "#415364",
  "Dark Blue": "#243746",
  Gray: "#adb7c0",
  Tan: "#a89c96",
  Copper: "#9e703c",
  Brown: "#7f6a45",
  White: "#ffffff",
  Black: "#000000",
};

// Other common apparel colours, after the brand ones.
export const OTHER_COLOURS = {
  Cream: "#f3ead3",
  Sand: "#d8c7a5",
  "Heather Gray": "#b6b8ba",
  Charcoal: "#36454f",
  Navy: "#1f2a44",
  "Royal Blue": "#2b50aa",
  Teal: "#0f7c80",
  "Kelly Green": "#2e9a4b",
  "Forest Green": "#2e5e3a",
  Olive: "#6b6b3a",
  Yellow: "#f2c318",
  Gold: "#c9a227",
  Orange: "#e8741e",
  Red: "#c62828",
  Maroon: "#6d1f2b",
  Burgundy: "#7b1f3a",
  Pink: "#e98fb4",
  Purple: "#5f3c8f",
};

export const NAMED_COLOURS = { ...BRAND_COLOURS, ...OTHER_COLOURS };

// Other spellings of the names above (one spelling is shown: Dylan's "Gray").
const ALIASES = { grey: "Gray", heathergrey: "Heather Gray" };

const squash = (s) =>
  String(s || "")
    .toLowerCase()
    .replace(/[^a-z]/g, "");
const NAMED_BY_KEY = Object.fromEntries([
  ...Object.entries(NAMED_COLOURS).map(([name, hex]) => [squash(name), hex]),
  ...Object.entries(ALIASES).map(([alias, name]) => [
    alias,
    NAMED_COLOURS[name],
  ]),
]);

function hexFromCode(text) {
  const m = String(text || "")
    .trim()
    .match(/^#?([0-9a-f]{3}|[0-9a-f]{6})$/i);
  if (!m) return null;
  const h = m[1].toLowerCase();
  return `#${
    h.length === 3
      ? h
          .split("")
          .map((c) => c + c)
          .join("")
      : h
  }`;
}

// CSS keyword -> #rrggbb via a 1x1 canvas. Null when the browser can't tell
// (no canvas, e.g. tests) or doesn't know the name.
function hexFromCss(name) {
  if (typeof document === "undefined") return null;
  let ctx = null;
  try {
    ctx = document.createElement("canvas").getContext("2d");
  } catch (err) {
    return null;
  }
  if (!ctx) return null;
  const sentinel = "#010203";
  ctx.fillStyle = sentinel;
  ctx.fillStyle = String(name).trim();
  const out = String(ctx.fillStyle).toLowerCase();
  if (out === sentinel && squash(name) !== "") return null;
  return /^#[0-9a-f]{6}$/.test(out) ? out : null;
}

/** "#rrggbb" for a name or code, or null when it isn't a colour. */
export function resolveColour(text) {
  const t = String(text || "").trim();
  if (!t) return null;
  return hexFromCode(t) || NAMED_BY_KEY[squash(t)] || hexFromCss(t);
}

/** The friendly name for a stored hex, when it's one of ours. */
export function nameForHex(hex) {
  const h = String(hex || "").toLowerCase();
  const hit = Object.entries(NAMED_COLOURS).find(([, v]) => v === h);
  return hit ? hit[0] : null;
}
